// PARA EL CONTADOR — el último mes cerrado, en una tabla que se puede mandar.
//
// Este bloque no descubre nada: repite números que ya están en la pantalla. Su
// razón de existir es otra, y es la que dicta cada decisión de acá abajo:
//
//   HOY EL DUEÑO LE SACA UNA FOTO CON EL TELÉFONO A LA PANTALLA Y SE LA MANDA
//   AL CONTADOR POR WHATSAPP.
//
// Por eso `Copiar` va ANTES que `Descargar`: la foto se reemplaza con un texto
// pegado en un mail o en un chat, no con un archivo que en el teléfono hay que
// ir a buscar a la carpeta de descargas.
//
// Tres reglas que este bloque no puede romper:
//
// · UNA SOLA FUENTE PARA PANTALLA, TEXTO Y PLANILLA. Las tres salen del mismo
//   arreglo `filas`. Si el mail dijera algo distinto de lo que el dueño vio en
//   pantalla, la que mintió es la pantalla. Nunca armar el texto por separado
//   "que total es lo mismo": deja de serlo en el primer cambio.
//
// · LA TABLA TIENE QUE CERRAR. El resultado se calcula como la resta exacta de
//   las dos filas de arriba, aunque el documento traiga su propio `net`. Una
//   tabla que no cierra no se le puede presentar a un contador, y ahí la
//   discusión pasa a ser sobre la pantalla y no sobre el mes.
//
// · SI LOS GASTOS NO ESTÁN CARGADOS, NO HAY RESULTADO. Medido sobre un cliente
//   real, `expense` viene cargado en el 9% de los días. Con esa cobertura el
//   neto sale idéntico al bruto y la tabla estaría afirmando que el
//   estacionamiento no tiene gastos. Por debajo del 30% de los días con algún
//   egreso cargado, la fila del resultado NO se dibuja y se dice por qué.

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Bloque from "./Bloque";
import { UI, ORIGEN } from "./theme";
import { formatCurrency, mesLargo, fecha, entero } from "./format";

// De ORIGEN se toman los RÓTULOS, no los colores: esta tabla no lleva color.
// El color acá no distingue nada —no hay series que confundir— y sólo lograría
// que la captura de pantalla se vea como un gráfico en vez de como un resumen.
const ROTULO = {
  tickets: ORIGEN.tickets.label,
  abonados: ORIGEN.abonados.label,
  convenios: ORIGEN.convenios.label,
  otros: ORIGEN.otros.label,
};

// Por debajo de esto el neto es una afirmación que la pantalla sabe falsa.
const COBERTURA_MINIMA_EGRESOS = 0.3;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const hoyISO = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  // Local, no toISOString(): eso es UTC y después de las 21:00 en Argentina
  // devuelve el día siguiente, o sea que "el mes en curso" se adelantaría el 31.
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const capitalizar = (s) => (s && s.length ? s[0].toUpperCase() + s.slice(1) : s);

/** "2026-08" → "2026-07". */
const mesPrevio = (ym) => {
  const [a, m] = String(ym).split("-").map((x) => parseInt(x, 10));
  const d = new Date(a, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** "2026-08" → "2026-09". */
const mesSiguiente = (ym) => {
  const [a, m] = String(ym).split("-").map((x) => parseInt(x, 10));
  const d = new Date(a, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const diasEnMes = (ym) => {
  const [a, m] = String(ym).split("-").map((x) => parseInt(x, 10));
  return new Date(a, m, 0).getDate();
};

/**
 * Los campos que esta tabla necesita, leídos de un documento —diario o mensual—
 * con los mismos nombres reales del Desktop.
 *
 * `tickets.total.*` es la suma exacta de las cuatro modalidades (está verificado
 * contra el Desktop), así que acá se usa el total y las modalidades quedan como
 * respaldo por si un documento viejo no lo trae. Desglosar por modalidad es
 * trabajo del bloque de composición, no de un resumen contable.
 */
function leerDoc(d) {
  const t = d?.tickets || {};
  const porModalidad = ["hour", "stay", "tn", "plana"].reduce((a, k) => a + num(t[k]?.amount), 0);
  return {
    income: num(d?.income),
    // `expense` llega como valor absoluto de los retiros, pero un signo invertido
    // en el origen convertiría los gastos en un ingreso más. Se fuerza el signo.
    expense: Math.abs(num(d?.expense)),
    tickets: num(t.total?.amount) || porModalidad,
    ticketsCobrados: num(t.total?.out),
    abonados: num(d?.monthly?.amount),
    convenios: num(d?.deals?.amount),
    manual: num(d?.manual?.amount),
    anulados: num(d?.canceled_count) || num(t.total?.canceled),
    formas: d?.payments && typeof d.payments === "object" ? d.payments : {},
  };
}

/** Suma varios días en un único resumen con la misma forma que `leerDoc`. */
function sumarDias(dias) {
  const base = {
    income: 0, expense: 0, tickets: 0, ticketsCobrados: 0,
    abonados: 0, convenios: 0, manual: 0, anulados: 0, formas: {},
  };
  dias.forEach((d) => {
    const x = leerDoc(d);
    base.income += x.income;
    base.expense += x.expense;
    base.tickets += x.tickets;
    base.ticketsCobrados += x.ticketsCobrados;
    base.abonados += x.abonados;
    base.convenios += x.convenios;
    base.manual += x.manual;
    base.anulados += x.anulados;
    // Las formas de pago son claves LIBRES, escritas por el cliente en su
    // idioma. No hay catálogo contra el cual mapearlas: se acumulan tal cual
    // vienen y se muestran tal cual las escribió.
    Object.entries(x.formas).forEach(([k, v]) => {
      base.formas[k] = (base.formas[k] || 0) + num(v);
    });
  });
  return base;
}

/**
 * ¿Este origen de plata existe para este cliente?
 *
 * Un cliente que nunca tuvo abonados no necesita ver "Abonados: $0" en la tabla
 * que le manda al contador: es una fila que no dice nada y ocupa un renglón en
 * la foto del teléfono. Pero un cliente que SÍ tuvo abonados y este mes cobró
 * cero necesita ver ese cero, porque ahí sí pasó algo.
 */
const algunaVez = (cerrados, f) => cerrados.some((d) => num(f(d)) > 0);

const ParaElContador = ({ mensuales = [], cerrados = [], nombre = null }) => {
  const [copiado, setCopiado] = useState(false);
  const timerRef = useRef(null);

  const datos = useMemo(() => {
    const mesEnCurso = hoyISO().slice(0, 7);

    // Los mensuales llegan ordenados descendente, pero no se confía en el orden
    // de la consulta: si mañana alguien cambia el `orderBy`, este bloque le
    // estaría mostrando al contador un mes cualquiera sin avisar.
    const cerradosMens = mensuales
      .filter((m) => m?.month && String(m.month) < mesEnCurso)
      .sort((a, b) => String(b.month).localeCompare(String(a.month)));

    const anterior = mesPrevio(mesEnCurso);
    const docMes = cerradosMens[0] || null;

    // El Desktop consolida el mes recién entre el día 1 y el 3 del siguiente.
    // Entre medio, `monthly_YYYY-MM` del mes que acaba de terminar todavía no
    // existe. Sin este respaldo, todos los meses habría tres días en los que el
    // bloque le dice "no hay nada" a un cliente que operó los 31 días.
    const diasDelAnterior = cerrados.filter((d) => String(d?.date || "").slice(0, 7) === anterior);
    const usarDias = (!docMes || String(docMes.month) !== anterior) && diasDelAnterior.length > 0;

    const ym = usarDias ? anterior : docMes ? String(docMes.month) : null;
    if (!ym) {
      return { vacio: true, mesEnCurso };
    }

    const r = usarDias ? sumarDias(diasDelAnterior) : leerDoc(docMes);
    const diasDelMes = cerrados.filter((d) => String(d?.date || "").slice(0, 7) === ym);

    // Días con movimiento, no días de calendario. Contar los cerrados como días
    // de $0 hunde el promedio de cualquiera que descanse los domingos.
    const conMovimiento = diasDelMes.filter(
      (d) => num(d?.income) > 0 || num(d?.tickets?.total?.in) > 0
    ).length;
    const diasDeclarados = num(usarDias ? diasDelAnterior.length : docMes?.days);
    const diasConMovimiento = conMovimiento || diasDeclarados || null;

    // ── Cobertura de egresos ────────────────────────────────────────────
    //
    // Se mide sobre los días DE ESTE MES cuando están disponibles, porque es
    // este mes el que se va a presentar. Para un mes viejo, cuyos diarios ya
    // vencieron por TTL, se cae a la cobertura general del cliente: es una
    // aproximación, y por eso se rotula distinto.
    const conEgreso = diasDelMes.filter((d) => Math.abs(num(d?.expense)) > 0).length;
    const baseCobertura = diasDelMes.length || cerrados.length;
    const conEgresoGlobal = cerrados.filter((d) => Math.abs(num(d?.expense)) > 0).length;
    const coberturaEgresos = baseCobertura
      ? (diasDelMes.length ? conEgreso : conEgresoGlobal) / baseCobertura
      : 0;
    const coberturaDelMes = diasDelMes.length > 0;
    const mostrarResultado = coberturaEgresos >= COBERTURA_MINIMA_EGRESOS;

    // ── Reparto por origen ──────────────────────────────────────────────
    //
    // Una fila de `cash` puede tener `ticket_id` y `deal_id` a la vez (un ticket
    // cobrado contra un convenio), así que las cuatro fuentes PUEDEN sumar más
    // que `income`. El total que manda es siempre `income`; el reparto es
    // informativo y, cuando se solapa, se dice en el pie. Lo que nunca se hace
    // es inventar un "Otros" negativo para forzar que cierre.
    const identificado = r.tickets + r.abonados + r.convenios + r.manual;
    const residuo = r.income - identificado;
    const solapamiento = residuo < -(r.income * 0.02);
    const otros = r.manual + Math.max(0, residuo);

    const resultado = r.income - r.expense;

    const filas = [];
    const F = (seccion, concepto, opts = {}) =>
      filas.push({ seccion, concepto, importe: null, cantidad: null, nota: null, total: false, ...opts });

    F("Facturado", ROTULO.tickets, { importe: r.tickets });
    if (r.abonados > 0 || algunaVez(cerrados, (d) => d?.monthly?.amount)) {
      F("Facturado", ROTULO.abonados, { importe: r.abonados });
    }
    if (r.convenios > 0 || algunaVez(cerrados, (d) => d?.deals?.amount)) {
      F("Facturado", ROTULO.convenios, { importe: r.convenios });
    }
    if (otros > 0 || algunaVez(cerrados, (d) => d?.manual?.amount)) {
      F("Facturado", ROTULO.otros, { importe: otros });
    }
    F("Facturado", "Total facturado", {
      importe: r.income,
      total: true,
      nota: "Lo que entró por caja. No incluye el fondo de caja.",
    });

    F("Gastos", "Egresos cargados", {
      importe: r.expense,
      nota: mostrarResultado
        ? "Retiros y gastos cargados en la caja de TicketControl."
        : null,
    });

    if (mostrarResultado) {
      F("Resultado", "Te quedó", {
        importe: resultado,
        total: true,
        nota: "Total facturado menos los egresos cargados.",
      });
    }

    const formas = Object.entries(r.formas)
      .map(([k, v]) => [k, num(v)])
      .filter(([, v]) => v !== 0)
      .sort((a, b) => b[1] - a[1]);
    formas.forEach(([k, v]) => F("Cómo te pagaron", k, { importe: v }));

    F("Movimiento", "Tickets cobrados", { cantidad: r.ticketsCobrados });
    F("Movimiento", "Tickets anulados", {
      cantidad: r.anulados,
      // Sin esta línea la tabla acusa. Anular es también la forma de corregir un
      // error de carga, y el contador que lo lea sin contexto va a preguntar.
      nota: r.anulados > 0 ? "Anular no es necesariamente un problema: puede ser un error de carga corregido." : null,
    });

    // ── Control contra la suma de los días ──────────────────────────────
    //
    // Sólo se compara cuando están TODOS los días del mes: si falta uno, la
    // diferencia la explica ese día que falta y el aviso sería una falsa alarma
    // sobre el dato que el dueño está por presentar.
    let descuadre = null;
    if (!usarDias && diasDelMes.length === diasEnMes(ym)) {
      const sumado = sumarDias(diasDelMes).income;
      if (r.income > 0 && Math.abs(sumado - r.income) / r.income > 0.01) {
        descuadre = sumado;
      }
    }

    return {
      vacio: false,
      ym,
      armadoDeDias: usarDias,
      filas,
      diasConMovimiento,
      diasDelMes: diasEnMes(ym),
      mostrarResultado,
      coberturaEgresos,
      coberturaDelMes,
      solapamiento,
      descuadre,
      income: r.income,
    };
  }, [mensuales, cerrados]);

  // ── Texto para pegar y planilla para bajar ────────────────────────────
  //
  // Las dos salen de `filas`, igual que la tabla de la pantalla. Es la única
  // forma de garantizar que lo que el contador recibe sea exactamente lo que el
  // dueño vio antes de mandarlo.
  const textos = useMemo(() => {
    if (datos.vacio) return null;

    const titulo = `${capitalizar(mesLargo(datos.ym))} · mes cerrado`;
    const encabezado = [
      "Para el contador",
      nombre ? `${nombre} — ${titulo}` : titulo,
      `${fecha(`${datos.ym}-01`)} al ${fecha(`${datos.ym}-${String(datos.diasDelMes).padStart(2, "0")}`)}`,
    ];

    const avisoGastos = !datos.mostrarResultado
      ? "No estás cargando los gastos, así que esto es lo que facturaste, no lo que te queda."
      : null;

    // Texto plano: rótulo y monto en el mismo renglón. Nada de alinear con
    // espacios — en WhatsApp la fuente es proporcional y la columna se rompe.
    const lineas = [...encabezado, ""];
    let seccionActual = null;
    datos.filas.forEach((f) => {
      if (f.seccion !== seccionActual) {
        if (seccionActual) lineas.push("");
        lineas.push(f.seccion.toUpperCase());
        seccionActual = f.seccion;
      }
      const valor = f.importe !== null ? formatCurrency(f.importe) : entero(f.cantidad);
      lineas.push(`${f.concepto}: ${valor}`);
    });
    if (datos.solapamiento) {
      lineas.push("");
      lineas.push("Hay cobros que entran en más de una categoría (por ejemplo un ticket cobrado por convenio), así que el reparto suma más que el total facturado. El total es el que manda.");
    }
    if (avisoGastos) {
      lineas.push("");
      lineas.push(avisoGastos);
    }

    // Planilla: `;` de separador y coma decimal, que es como abre Excel en
    // español sin pedir nada. Y el importe va CRUDO, sin símbolo ni separador de
    // miles: con el símbolo adentro la celda entra como texto y el contador no
    // puede sumar la columna, que es lo único que va a querer hacer con esto.
    const csvNum = (n) => (n === null ? "" : String(Math.round(num(n) * 100) / 100).replace(".", ","));
    const limpio = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = [["Mes", "Seccion", "Concepto", "Importe", "Cantidad"].join(";")];
    datos.filas.forEach((f) => {
      csv.push([datos.ym, limpio(f.seccion), limpio(f.concepto), csvNum(f.importe), csvNum(f.cantidad)].join(";"));
    });
    if (avisoGastos) {
      // Va como fila y no como pie de página: un renglón suelto al final del
      // archivo rompe el parseo, una fila con las mismas columnas no.
      csv.push([datos.ym, limpio("Nota"), limpio(avisoGastos), "", ""].join(";"));
    }

    return { texto: lineas.join("\n"), csv: csv.join("\r\n"), avisoGastos };
  }, [datos, nombre]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const copiar = async () => {
    if (!textos) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textos.texto);
      } else {
        // Sin HTTPS o en navegadores viejos no existe la API moderna. El botón
        // tiene que funcionar igual: es la acción principal del bloque.
        const ta = document.createElement("textarea");
        ta.value = textos.texto;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiado(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiado(false), 4000);
    } catch (e) {
      console.error("Stats2: no se pudo copiar el resumen", e);
    }
  };

  const descargar = () => {
    if (!textos) return;
    // El BOM no es un adorno: sin él, Excel abre el archivo en la codificación
    // del sistema y "Anulaciones" y los símbolos de moneda salen rotos.
    const blob = new Blob(["\uFEFF" + textos.csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contador_${datos.ym}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Estado vacío ──────────────────────────────────────────────────────
  //
  // No se dibuja una tabla en cero: diría que el mes pasado no entró plata, que
  // es distinto de que el mes todavía no se cerró.
  if (datos.vacio) {
    return (
      <Bloque
        nombre="Para el contador"
        id="para-el-contador"
        titular="Todavía no hay ningún mes cerrado para pasarle al contador"
        frase={
          <>
            El resumen se arma solo cuando el mes termina: el de{" "}
            {capitalizar(mesLargo(datos.mesEnCurso))} va a aparecer en los primeros días de{" "}
            {capitalizar(mesLargo(mesSiguiente(datos.mesEnCurso)))}. No hay nada que configurar.
          </>
        }
      />
    );
  }

  const acciones = (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {copiado && (
        <span style={{ fontSize: 12.5, color: UI.textoSuave }} role="status">
          Copiado. Ya lo puedes pegar en un mail o en WhatsApp.
        </span>
      )}
      {/* Copiar primero, y no por costumbre: hoy el dueño resuelve esto sacándole
          una foto a la pantalla con el teléfono. Lo que reemplaza a la foto es un
          texto que se pega en el chat, no un archivo en la carpeta de descargas. */}
      <button type="button" className="btn btn-primary btn-sm" onClick={copiar}>
        <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
          content_copy
        </span>
        Copiar
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={descargar}>
        <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
          download
        </span>
        Descargar CSV
      </button>
    </div>
  );

  const tabla = (
    <>
      {/* La tabla scrollea adentro de su caja. Si desbordara la página, en el
          teléfono se movería la pantalla entera y el bloque quedaría inusable. */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 280 }}>
          <tbody>
            {datos.filas.map((f, i) => {
              const nuevaSeccion = i === 0 || datos.filas[i - 1].seccion !== f.seccion;
              return (
                <React.Fragment key={`${f.seccion}-${f.concepto}`}>
                  {nuevaSeccion && (
                    <tr>
                      <th
                        colSpan={2}
                        style={{
                          fontSize: 11, fontWeight: 700, color: UI.textoSuave,
                          textTransform: "uppercase", letterSpacing: 0.5,
                          padding: i === 0 ? "0 0 6px" : "16px 0 6px", textAlign: "left",
                        }}
                      >
                        {f.seccion}
                      </th>
                    </tr>
                  )}
                  <tr style={{ borderTop: `1px solid ${UI.linea}` }}>
                    <td style={{ padding: "7px 8px 7px 0", color: UI.texto, fontWeight: f.total ? 700 : 400 }}>
                      {f.concepto}
                      {f.nota && (
                        <div style={{ fontSize: 11.5, color: UI.textoSuave, lineHeight: 1.35, marginTop: 1 }}>
                          {f.nota}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "7px 0", textAlign: "right", whiteSpace: "nowrap",
                        color: UI.texto, fontWeight: f.total ? 700 : 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {f.importe !== null ? formatCurrency(f.importe) : entero(f.cantidad)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {datos.solapamiento && (
        <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "10px 0 0", lineHeight: 1.45 }}>
          El reparto de arriba suma más que el total facturado. Hay cobros que entran en más de
          una categoría, por ejemplo un ticket cobrado por convenio. El que manda es el total.
        </p>
      )}

      {!datos.mostrarResultado && (
        <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "10px 0 0", lineHeight: 1.45 }}>
          No estás cargando los gastos, así que esto es lo que facturaste, no lo que te queda.
          Los egresos se cargan en TicketControl, en la caja, como movimiento de salida.{" "}
          <Link to="/cash" style={{ fontWeight: 600 }}>Ver tu caja</Link>
          {datos.coberturaDelMes
            ? ` · este mes hay egresos cargados en ${Math.round(datos.coberturaEgresos * 100)}% de los días.`
            : ` · en tu historia hay egresos cargados en ${Math.round(datos.coberturaEgresos * 100)}% de los días.`}
        </p>
      )}

      {datos.descuadre !== null && (
        <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "10px 0 0", lineHeight: 1.45 }}>
          Sumando día por día ese mes da {formatCurrency(datos.descuadre)}, y el resumen del mes
          dice {formatCurrency(datos.income)}. Antes de presentarlo, revisa el mes en{" "}
          <Link to="/resumeDay" style={{ fontWeight: 600 }}>Resumen diario</Link>.
        </p>
      )}
    </>
  );

  // El titular es la conclusión, y la conclusión de este bloque es el número que
  // el dueño va a tener que explicarle al contador. Se lee de `filas`, que ya
  // está calculado: armar la cuenta de nuevo acá sería la segunda fuente que el
  // encabezado del archivo prohíbe.
  const filaResultado = datos.filas.find((f) => f.concepto === "Te quedó");

  return (
    <Bloque
      nombre="Para el contador"
      id="para-el-contador"
      titular={
        filaResultado
          ? `En ${mesLargo(datos.ym)} facturaste ${formatCurrency(datos.income)} y te quedaron ${formatCurrency(filaResultado.importe)}`
          : `En ${mesLargo(datos.ym)} facturaste ${formatCurrency(datos.income)}`
      }
      frase={
        <>
          {filaResultado
            // Sin la fila de resultado el número de arriba es sólo lo facturado, y
            // eso hay que decirlo en el mismo renglón: un dueño que lee "facturaste"
            // y piensa "gané" es exactamente el malentendido que este bloque causa.
            ? "Es el último mes cerrado. Cópialo y pégalo en un mail o en un chat, o descárgalo como planilla."
            : "Es el último mes cerrado, y es lo que facturaste, no lo que te queda: los gastos casi no están cargados. Cópialo y pégalo en un mail o en un chat, o descárgalo como planilla."}
          {datos.armadoDeDias && (
            // Se dice, no se disimula: entre el 1 y el 3 el Desktop todavía no
            // consolidó el mes, así que esto es la suma de los días. Un número
            // armado distinto que se presenta igual es exactamente la clase de
            // cosa que después nadie puede explicar.
            <> Está sumado día por día: el resumen del mes todavía no se consolidó en la PC.</>
          )}
        </>
      }
      rango={`${fecha(`${datos.ym}-01`)} – ${fecha(`${datos.ym}-${String(datos.diasDelMes).padStart(2, "0")}`)}${
        datos.diasConMovimiento
          ? ` · ${entero(datos.diasConMovimiento)} ${datos.diasConMovimiento === 1 ? "día" : "días"} con movimiento`
          : ""
      }`}
      acciones={acciones}
      detalle={tabla}
      detalleLabel="Ver la tabla del mes"
    />
  );
};

export default ParaElContador;
